<template>
  <b-modal
    :id="id"
    centered
    scrollable
    :title="modalTitle"
    modal-class="share-modal"
    data-test="creative-share-modal"
  >
    <div
      class="p-4 mb-3"
    >
      <label

        for="user-email-input"
        class="font-weight-bold fn-size-15"
      >
        Add users
        <i
          id="add-users-info-icon"
          class="fal fa-info-circle text-muted ml-1"
          v-b-tooltip.hover
          title="Add someone by email, then choose which access types (Campaign, Creative, Reporting) to grant them below."
        />
      </label>

      <div
        class="d-flex align-items-start"
        style="gap: 0.75rem;"
      >
        <b-form-input
          id="user-email-input"
          placeholder="Email Address"
          v-model="userEmail"
          type="email"
          :class="[!emailInSharedWithList ? '' : 'is-invalid']"
        />
        <b-button
          data-test="add-user-email"
          variant="primary"
          @click="addEmailToShareList"
          :disabled="isAddDisabled"
          :class="{ 'btn-loading': isAddButtonLoading }"
        >
          Add
        </b-button>
      </div>

      <div
        style="font-size: 0.75rem;"
        class="mt-2"
      >
        <p
          v-if="emailInSharedWithList && !isAddButtonLoading"
          :class="{'invalid': emailInSharedWithList}"
        >
          The entered email is already in the "Share With" list below.
        </p>
        <p
          v-else-if="!emailInSharedWithList && !isAddButtonLoading"
        >
          {{ context === 'campaign'
            ? 'Enter the email addresses of the people you wish to share the campaign with.'
            : 'Enter the email addresses of the people you wish to give access to the campaign group.' }}
        </p>
      </div>
    </div>
    <div
      v-if="isShareWithEmailsListLoading"
      class="loader mx-auto"
      style="height: 300px"
    />
    <div
      class="p-4"
      v-else-if="alreadySharedWithEmails.length > 0 || totalSharedWithEmails.length > 0"
    >
      <div
        id="share-with-emails-list-container"
      >
        <ul
          id="share-with-emails-list"
          class="m-0 p-0"
        >
          <share-modal-emails-list-item
            v-for="(emailData, index) in totalSharedWithEmails"
            class="share-with-emails-item"
            :key="emailData.email"
            :index="index"
            :resend-enabled="isResendEnabled(emailData.email)"
            :context="context"
            :campaign-id="campaignId"
            :advertiser-id="advertiserId"
            :group="group"
            :current-types="context === 'group' ? emailData.groupCurrentTypes : emailData.currentTypes"
            :campaigns="campaigns"
            :campaign-permissions="emailData.campaignPermissions || {}"
            :is-expanded="emailData.isExpanded || false"
            :email="emailData.email"
            @removeEmail="removeEmail"
            @update:currentTypes="updateCurrentTypes(emailData, $event)"
            @update:campaignPermissions="updateCampaignPermissions(emailData, $event)"
            @resendSuccess="handleResendSuccess"
            @apiError="handleApiError"
          />
        </ul>
      </div>
    </div>
    <template #modal-footer="{ cancel }">
      <b-button
        variant="link"
        size="sm"
        @click="cancel()"
      >
        Cancel
      </b-button>
      <b-button
        id="share-modal-invite"
        variant="primary"
        size="sm"
        type="button"
        @click="inviteEmails($event)"
        :disabled="isInviteDisabled"
        :class="{ 'btn-loading': isInviteButtonLoading }"
      >
        Update
      </b-button>
    </template>
  </b-modal>
</template>
<script>
import signUtils from '@/core/signUtils';
import { mapActions, mapState, mapMutations } from 'vuex';
import * as shareTypes from '@/modules/share/store/types';
import * as types from '@/store/types';
// eslint-disable-next-line import/no-cycle
import campaignApi from '@/modules/campaigns/api/campaignApi';

import ShareModalEmailsListItem from '@/modules/share/components/ShareModalEmailsListItem';

function initialState() {
  return {
    isShareWithEmailsListLoading: false,
    emailInSharedWithList: false,
    isAddButtonLoading: false,
    isInviteButtonLoading: false,
    userEmail: '',
    alreadySharedWithEmails: [],
    userShareList: [],
    // Fetched directly by campaignId (see loadCampaignName) rather than relying
    // on the Vuex `campaign` state, since that state is only populated when
    // viewing/editing a single campaign (EditCampaign/NewCampaign) and is empty
    // when this modal is opened from a dashboard list row.
    fetchedCampaignName: '',
  };
}

export default {
  name: 'ShareModal',
  components: {
    ShareModalEmailsListItem,
  },
  props: {
    id: {
      type: String,
      default: 'modalSharing',
    },
    context: {
      type: String,
      default: 'campaign',
    },
    campaignId: {
      type: Number,
      default: 0,
    },
    advertiserId: {
      type: Number,
      default: 0,
    },
    group: {
      type: String,
      default: '',
    },
    campaigns: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return initialState();
  },
  computed: {
    ...mapState('campaigns', [
      'campaign',
    ]),
    modalTitle() {
      if (this.context === 'campaign') {
        return `Campaign Access: ${this.fetchedCampaignName || ''}`;
      }
      return `Campaign Group Access: ${this.group}`;
    },
    isAddDisabled() {
      return !this.isEmailValid() || this.isAddButtonLoading;
    },
    isInviteDisabled() {
      if (this.isShareWithEmailsListLoading || this.isInviteButtonLoading) return true;
      if (this.context === 'group') {
        // Enabled when a new email has any type checked (group-level or per-campaign)
        const hasNewEmailWithType = this.userShareList.some((e) => this.groupEntryHasAnyType(e));
        if (hasNewEmailWithType) return false;
        // Enabled when an existing sharee changed anything (group-level or per-campaign)
        return !this.alreadySharedWithEmails.some((e) => this.groupEntryChanged(e));
      }
      // Enabled when at least one new email has ≥1 type checked
      const hasNewEmailWithType = this.userShareList.some((e) => e.currentTypes && e.currentTypes.length > 0);
      if (hasNewEmailWithType) return false;
      // Enabled when at least one existing sharee has any changed checkbox
      const hasChangedExisting = this.alreadySharedWithEmails.some((e) => {
        const orig = e.originalTypes || [];
        const curr = e.currentTypes || [];
        if (orig.length !== curr.length) return true;
        return orig.some((t) => !curr.includes(t)) || curr.some((t) => !orig.includes(t));
      });
      return !hasChangedExisting;
    },
    totalSharedWithEmails() {
      const merged = [...this.userShareList, ...this.alreadySharedWithEmails];
      return merged.sort((a, b) => a.email.localeCompare(b.email));
    },
    isSelfServiceCampaign() {
      return this.campaign?.type === 'SELF_SERVICE';
    },
  },
  methods: {
    ...mapActions('share', [
      shareTypes.FETCH_SHARE_WITH_EMAILS_LIST,
      shareTypes.UPDATE_SHARE_WITH_EMAILS_LIST,
      shareTypes.REMOVE_EMAIL_FROM_SHARE_WITH,
      shareTypes.FETCH_GROUP_SHARES,
      shareTypes.UPDATE_GROUP_SHARES,
      shareTypes.REMOVE_GROUP_SHARE_FOR_EMAIL,
      shareTypes.RECONCILE_GROUP_CAMPAIGN_ON_REVOKE,
    ]),
    ...mapMutations([
      types.SET_GLOBAL_MESSAGE,
    ]),
    openModal() {
      this.loadData();
      this.$bvModal.show(this.id);
    },
    // CAMPAIGN access is a superset of Creative (SHOWCASE) and Reporting — anyone
    // with CAMPAIGN already implicitly has both. Whenever a types array contains
    // CAMPAIGN, ensure SHOWCASE and REPORTING are present too, so the checkboxes
    // reflect the implied access even if the backend data only recorded CAMPAIGN.
    withCampaignSuperset(shareTypesList = []) {
      if (!shareTypesList.includes('CAMPAIGN')) return shareTypesList;
      return [...new Set([...shareTypesList, 'SHOWCASE', 'REPORTING'])];
    },
    // Applied on load so the modal opens with checkboxes already reflecting the
    // CAMPAIGN → Creative/Reporting implication, both for the campaign modal's
    // flat currentTypes/originalTypes and the group modal's group-level and
    // per-campaign types. Both originalTypes and currentTypes are expanded
    // identically so this normalization alone never enables the Update button.
    applyCampaignSupersetOnLoad(sharedWithEmails) {
      if (this.context === 'group') {
        return sharedWithEmails.map((entry) => {
          const campaignPermissions = {};
          Object.keys(entry.campaignPermissions || {}).forEach((campaignId) => {
            const permission = entry.campaignPermissions[campaignId];
            campaignPermissions[campaignId] = {
              originalTypes: this.withCampaignSuperset(permission.originalTypes),
              currentTypes: this.withCampaignSuperset(permission.currentTypes),
            };
          });
          return {
            ...entry,
            groupOriginalTypes: this.withCampaignSuperset(entry.groupOriginalTypes),
            groupCurrentTypes: this.withCampaignSuperset(entry.groupCurrentTypes),
            campaignPermissions,
          };
        });
      }
      return sharedWithEmails.map((entry) => ({
        ...entry,
        originalTypes: this.withCampaignSuperset(entry.originalTypes),
        currentTypes: this.withCampaignSuperset(entry.currentTypes),
      }));
    },
    async loadCampaignName() {
      try {
        const campaign = await campaignApi.getCampaignNameById(this.campaignId);
        this.fetchedCampaignName = campaign?.name || '';
      } catch (error) {
        this.fetchedCampaignName = '';
      }
    },
    async loadData() {
      if (this.context === 'campaign') {
        // Guard: don't load if campaign ID is not available
        if (!this.campaignId) {
          console.warn('ShareModal: Cannot load data - campaign ID is not available');
          return;
        }

        await this.loadCampaignName();

        this.isShareWithEmailsListLoading = true;
        try {
          const sharedWithEmails = await this.FETCH_SHARE_WITH_EMAILS_LIST({
            campaignId: this.campaignId,
          });
          this.alreadySharedWithEmails = this.applyCampaignSupersetOnLoad(sharedWithEmails);
        } catch (error) {
          this.handleApiError(error);
        } finally {
          this.isShareWithEmailsListLoading = false;
        }
      }
      if (this.context === 'group') {
        this.isShareWithEmailsListLoading = true;
        try {
          const sharedWithEmails = await this.FETCH_GROUP_SHARES({
            advertiserId: this.advertiserId,
            group: this.group,
            campaigns: this.campaigns,
          });
          this.alreadySharedWithEmails = this.applyCampaignSupersetOnLoad(sharedWithEmails);
        } catch (error) {
          this.handleApiError(error);
        } finally {
          this.isShareWithEmailsListLoading = false;
        }
      }
    },
    resetData() {
      Object.assign(this.$data, initialState());
    },
    trimEmail() {
      this.userEmail = this.userEmail.trim();
    },
    isEmailValid() {
      return signUtils.isEmailValid(this.userEmail);
    },
    isEmailAlreadyShared(email) {
      return this.alreadySharedWithEmails.some((e) => e.email === email);
    },
    isResendEnabled(email) {
      return this.isEmailAlreadyShared(email);
    },
    addEmailToShareList() {
      this.isAddButtonLoading = true;
      this.trimEmail();

      const emailExists = this.totalSharedWithEmails.some((e) => e.email === this.userEmail);
      if (emailExists) {
        this.emailInSharedWithList = true;
        this.isAddButtonLoading = false;
        return;
      }

      this.userShareList.push(this.buildNewShareEntry(this.userEmail));
      this.emailInSharedWithList = false;
      this.userEmail = '';
      this.isAddButtonLoading = false;
    },
    buildNewShareEntry(email) {
      if (this.context === 'group') {
        const campaignPermissions = {};
        this.campaigns.forEach((campaign) => {
          campaignPermissions[campaign.id] = { originalTypes: [], currentTypes: [] };
        });
        return {
          email,
          groupOriginalTypes: [],
          groupCurrentTypes: [],
          campaignPermissions,
          isExpanded: false,
        };
      }
      return { email, currentTypes: [] };
    },
    updateCurrentTypes(emailData, newTypes) {
      // Use $set to ensure Vue 2 reactivity when mutating a nested property.
      // In group context the top-level checkboxes represent group-level access.
      const field = this.context === 'group' ? 'groupCurrentTypes' : 'currentTypes';
      this.$set(emailData, field, newTypes);
    },
    updateCampaignPermissions(emailData, newPermissions) {
      this.$set(emailData, 'campaignPermissions', newPermissions);
      this.reconcileGroupLevelType(emailData);
    },
    // Group-level access for a type only makes sense while EVERY campaign in the
    // group has that type, and conversely should be granted automatically once
    // every campaign does have it. Reconcile in both directions after any
    // per-campaign checkbox change:
    //  - If the group-level checkbox is checked but at least one campaign no
    //    longer has the type, untick it at the group level (surfaces as a
    //    removedType in the group-level diff on Update -> deleteGroupShare).
    //  - If every campaign in the group now has the type but the group-level
    //    checkbox isn't checked yet, tick it at the group level (surfaces as an
    //    addedType in the group-level diff on Update -> postGroupShare).
    reconcileGroupLevelType(emailData) {
      if (this.context !== 'group' || this.campaigns.length === 0) return;
      const permissions = emailData.campaignPermissions || {};
      ['CAMPAIGN', 'SHOWCASE', 'REPORTING'].forEach((type) => {
        const groupCurrent = emailData.groupCurrentTypes || [];
        const allCampaignsHaveType = this.campaigns.every((campaign) => {
          const permission = permissions[campaign.id];
          return permission && (permission.currentTypes || []).includes(type);
        });
        if (allCampaignsHaveType && !groupCurrent.includes(type)) {
          this.$set(emailData, 'groupCurrentTypes', [...groupCurrent, type]);
        } else if (!allCampaignsHaveType && groupCurrent.includes(type)) {
          this.$set(emailData, 'groupCurrentTypes', groupCurrent.filter((t) => t !== type));
        }
      });
    },
    async removeEmail(email) {
      // Pending (not yet saved) email: remove locally, no API call.
      const pendingIndex = this.userShareList.findIndex((e) => e.email === email);
      if (pendingIndex !== -1) {
        this.userShareList.splice(pendingIndex, 1);
        return;
      }

      const sharee = this.alreadySharedWithEmails.find((e) => e.email === email);
      if (!sharee) return;

      if (this.context === 'group') {
        // Remove the sharee from the group AND from every campaign in the group,
        // using the last-saved (original) types at each level.
        const groupTypes = sharee.groupOriginalTypes || [];
        const permissions = sharee.campaignPermissions || {};
        const campaignDeletes = Object.keys(permissions)
          .map((campaignId) => ({
            campaignId: Number(campaignId),
            types: permissions[campaignId].originalTypes || [],
          }))
          .filter((c) => c.types.length > 0);

        try {
          await this.REMOVE_GROUP_SHARE_FOR_EMAIL({
            advertiserId: this.advertiserId,
            group: this.group,
            email,
            groupTypes,
            campaignDeletes,
          });
          const index = this.alreadySharedWithEmails.findIndex((e) => e.email === email);
          if (index !== -1) this.alreadySharedWithEmails.splice(index, 1);
        } catch (error) {
          this.handleApiError(error);
        }
        return;
      }

      try {
        await this.REMOVE_EMAIL_FROM_SHARE_WITH({
          campaignId: this.campaignId,
          email,
          types: sharee.originalTypes || [],
        });

        // Same group-level reconciliation as Update: removing a sharee who had
        // CAMPAIGN on a grouped campaign must also dissolve their group-level
        // CAMPAIGN share so access isn't retained via the group.
        if (this.group && (sharee.originalTypes || []).includes('CAMPAIGN')) {
          await this.RECONCILE_GROUP_CAMPAIGN_ON_REVOKE({
            advertiserId: this.advertiserId,
            group: this.group,
            campaignId: this.campaignId,
            emails: [email],
          });
        }

        const index = this.alreadySharedWithEmails.findIndex((e) => e.email === email);
        if (index !== -1) this.alreadySharedWithEmails.splice(index, 1);
      } catch (error) {
        this.handleApiError(error);
      }
    },
    handleResendSuccess() {
      this.SET_GLOBAL_MESSAGE({
        message: 'Resent successfully',
        type: 'success',
      });
    },
    handleApiError() {
      this.SET_GLOBAL_MESSAGE({
        message: 'An error occurred',
        type: 'error',
      });
      this.loadData();
    },
    groupEntryHasAnyType(entry) {
      if ((entry.groupCurrentTypes || []).length > 0) return true;
      const permissions = entry.campaignPermissions || {};
      return Object.keys(permissions).some(
        (campaignId) => (permissions[campaignId].currentTypes || []).length > 0,
      );
    },
    groupEntryChanged(entry) {
      const differ = (a = [], b = []) => a.length !== b.length
        || a.some((t) => !b.includes(t))
        || b.some((t) => !a.includes(t));
      if (differ(entry.groupOriginalTypes, entry.groupCurrentTypes)) return true;
      const permissions = entry.campaignPermissions || {};
      return Object.keys(permissions).some((campaignId) => differ(
        permissions[campaignId].originalTypes,
        permissions[campaignId].currentTypes,
      ));
    },
    async inviteEmails(bvModalEvent) {
      bvModalEvent.preventDefault();
      if (this.context === 'group') {
        await this.inviteGroupEmails();
        return;
      }

      this.isInviteButtonLoading = true;
      try {
        // Compute diff for existing sharees: originalTypes vs currentTypes
        const existingDiff = this.alreadySharedWithEmails.map((e) => {
          const orig = e.originalTypes || [];
          const curr = e.currentTypes || [];
          return {
            email: e.email,
            addedTypes: curr.filter((t) => !orig.includes(t)),
            removedTypes: orig.filter((t) => !curr.includes(t)),
          };
        }).filter((d) => d.addedTypes.length > 0 || d.removedTypes.length > 0);

        // Compute diff for new emails: all checked types are "added"
        const newEmailsDiff = this.userShareList
          .filter((e) => e.currentTypes && e.currentTypes.length > 0)
          .map((e) => ({
            email: e.email,
            addedTypes: [...e.currentTypes],
            removedTypes: [],
          }));

        const diff = [...existingDiff, ...newEmailsDiff];

        await this.UPDATE_SHARE_WITH_EMAILS_LIST({
          campaignId: this.campaignId,
          diff,
        });

        // If this campaign belongs to a group and CAMPAIGN was revoked for a
        // sharee, the campaign-scoped DELETE alone can't remove access that came
        // from a group-level CAMPAIGN share — dissolve that group grant too.
        const emailsLosingCampaign = diff
          .filter((d) => (d.removedTypes || []).includes('CAMPAIGN'))
          .map((d) => d.email);
        if (this.group && emailsLosingCampaign.length > 0) {
          await this.RECONCILE_GROUP_CAMPAIGN_ON_REVOKE({
            advertiserId: this.advertiserId,
            group: this.group,
            campaignId: this.campaignId,
            emails: emailsLosingCampaign,
          });
        }

        // On success: merge userShareList into alreadySharedWithEmails, re-sort, reset originalTypes, clear userShareList
        this.userShareList.forEach((newEntry) => {
          this.alreadySharedWithEmails.push({
            email: newEntry.email,
            originalTypes: [...newEntry.currentTypes],
            currentTypes: [...newEntry.currentTypes],
          });
        });

        // Update originalTypes to match currentTypes for existing sharees
        this.alreadySharedWithEmails.forEach((e) => {
          e.originalTypes = [...e.currentTypes];
        });

        // Re-sort alphabetically
        this.alreadySharedWithEmails.sort((a, b) => a.email.localeCompare(b.email));

        // Clear pending list
        this.userShareList = [];

        // Close modal
        this.$bvModal.hide(this.id);

        // Show success message
        this.SET_GLOBAL_MESSAGE({
          message: 'Updates to campaign access successful!',
          type: 'success',
        });
      } catch (error) {
        this.handleApiError(error);
      } finally {
        this.isInviteButtonLoading = false;
      }
    },
    async inviteGroupEmails() {
      this.isInviteButtonLoading = true;
      try {
        // Group-level diff: existing sharees (groupOriginalTypes vs groupCurrentTypes)
        // plus new emails (all checked group types are "added").
        const groupDiff = [];
        this.alreadySharedWithEmails.forEach((e) => {
          const orig = e.groupOriginalTypes || [];
          const curr = e.groupCurrentTypes || [];
          const addedTypes = curr.filter((t) => !orig.includes(t));
          const removedTypes = orig.filter((t) => !curr.includes(t));
          if (addedTypes.length || removedTypes.length) {
            groupDiff.push({ email: e.email, addedTypes, removedTypes });
          }
        });
        this.userShareList.forEach((e) => {
          const curr = e.groupCurrentTypes || [];
          if (curr.length) {
            groupDiff.push({ email: e.email, addedTypes: [...curr], removedTypes: [] });
          }
        });

        // Per-campaign diff (per sharee, per campaign). New emails have no originals.
        const campaignDiff = [];
        const collectCampaignDiff = (entry, useOriginal) => {
          const permissions = entry.campaignPermissions || {};
          Object.keys(permissions).forEach((campaignId) => {
            const orig = useOriginal ? (permissions[campaignId].originalTypes || []) : [];
            const curr = permissions[campaignId].currentTypes || [];
            const addedTypes = curr.filter((t) => !orig.includes(t));
            const removedTypes = orig.filter((t) => !curr.includes(t));
            if (addedTypes.length || removedTypes.length) {
              campaignDiff.push({
                campaignId: Number(campaignId),
                email: entry.email,
                addedTypes,
                removedTypes,
              });
            }
          });
        };
        this.alreadySharedWithEmails.forEach((e) => collectCampaignDiff(e, true));
        this.userShareList.forEach((e) => collectCampaignDiff(e, false));

        await this.UPDATE_GROUP_SHARES({
          advertiserId: this.advertiserId,
          group: this.group,
          groupDiff,
          campaignDiff,
        });

        // On success: fold new emails into the saved list, then normalise originals to
        // match currents at both levels so the diff is clean on the next Update.
        this.userShareList.forEach((newEntry) => {
          this.alreadySharedWithEmails.push(newEntry);
        });
        this.alreadySharedWithEmails.forEach((e) => {
          e.groupOriginalTypes = [...(e.groupCurrentTypes || [])];
          const permissions = e.campaignPermissions || {};
          Object.keys(permissions).forEach((campaignId) => {
            permissions[campaignId].originalTypes = [...(permissions[campaignId].currentTypes || [])];
          });
        });

        this.alreadySharedWithEmails.sort((a, b) => a.email.localeCompare(b.email));
        this.userShareList = [];
        this.$bvModal.hide(this.id);

        this.SET_GLOBAL_MESSAGE({
          message: 'Campaign group access updated successfully!',
          type: 'success',
        });
      } catch (error) {
        this.handleApiError(error);
      } finally {
        this.isInviteButtonLoading = false;
      }
    },
  },
  mounted() {
    this.$root.$on('bv::modal::hide', (bvEvent, modalId) => {
      if (modalId === this.id) {
        this.resetData();
      }
    });
  },
  destroyed() {
    this.$root.$off('bv::modal::show');
    this.$root.$off('bv::modal::hidden');
  },
};
</script>
<style scoped lang="scss">
.fn-size-15 {
font-size: 15px;
}
.black-loader::after {
  border: 2px solid #000;
}
#share-with-emails-list-container {
  min-height: 300px;
  max-height: 40vh;
  overflow: scroll;
}
.share-with-emails-item {
  border: 1px solid #e3e8ef;
  border-radius: 8px;
  margin-bottom: 0.75rem;
}
.share-with-emails-item:last-child {
  margin-bottom: 0;
}
</style>
<style lang="scss">
.share-modal .modal-dialog {
    min-width: 700px;
}
</style>
